import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  Button, Checkbox, IconButton, MenuList, MenuItem,
} from '@material-ui/core';
import { Clear } from '@material-ui/icons';
import {
  compose, every, filter,
  find, get, getOr,
  groupBy, identity, includes,
  intersection, isEmpty, keyBy,
  keys, map, maxBy,
  mapValues, pickBy, reduce,
  size, sortBy, toNumber,
  unionBy, uniqBy,
} from 'lodash/fp';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Spinner,
} from '../../common/components';

const S = {
  Dialog: styled(Dialog)`
    .paper {
      position: relative;
      width: 600px;
    }
  `,
  DialogTitle: styled(DialogTitle)`
    margin-bottom: ${({ theme }) => theme.typography.pxToRem(14)};
  `,
  Heading: styled.h3`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(20)};
    font-weight: 500;
    line-height: 1.5;
  `,
  DialogContent: styled(DialogContent)`
    margin-bottom: 40px;
  `,
  Note: styled.p`
    margin-bottom: 8px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(12)};
    line-height: 1.5;
    text-align: right;
  `,
  Flexbox: styled.div`
    display: flex;
    justify-content: space-between;
    height: 292px;
    margin-bottom: 1rem;
  `,
  Padder: styled.div`
    width: 32%;
    padding-right: 8px;
  `,
  MenuList: styled(MenuList)`
    overflow: auto;
    height: 100%;
    border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    border-radius: 4px;
    outline: none;
  `,
  MenuItem: styled(MenuItem)`
    min-height: 40px;
    padding: 4px 8px;
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    line-height: 1.53;
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
  `,
  SubmenuItem: styled(MenuItem)`
    min-height: 40px;
    padding: 4px 8px 4px 32px;
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    line-height: 1.53;
    :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
  `,
  Checkbox: styled(({ margined, ...otherProps }) => <Checkbox {...otherProps} />)`
    padding: 4px;
    color: ${({ theme }) => theme.palette.grey[400]};
    ${({ margined }) => margined && css`
      margin-right: 4px;
    `}
    &.Mui-checked {
      color: ${({ theme }) => theme.palette.primary.light};
    }
  `,
  TargetCounter: styled.p`
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    line-height: 1.53;
    color: ${({ theme }) => theme.palette.grey[600]};
    text-align: right;
  `,
  Selected: styled.em`
    color: ${({ theme }) => theme.palette.secondary.main};
    font-style: normal;
  `,
  TargetList: styled.div`
    flex-grow: 1;
    overflow: auto;
    padding: 8px 0;
    border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    border-radius: 4px;
  `,
  Table: styled.table`
    border-collapse: collapse;
    width: 100%;
  `,
  TableHead: styled.thead`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 700;
    line-height: 1.54;
  `,
  TableBody: styled.tbody`
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 400;
    line-height: 1.53;
  `,
  TableRow: styled.tr`
    height: 40px;
  `,
  TableCell: styled.td`
    text-align: center;
  `,
  IconButton: styled(IconButton)`
    position: absolute;
    top: 2rem;
    right: 2rem;
    transform: translate(3px, -3px);
  `,
  Clear: styled(Clear)`
    font-size: 32px;
  `,
  Button: styled(Button)`
    min-width: 90px;
    min-height: 40px;
  `,
};

// TODO: 성능 문제로 인한 react-window 적용 필요
const TargetSelectDialog = ({
  form, groups, targets, open, onClose,
}) => {
  const [selectedGroups, setSelectedGroups] = useState({
    clazzGroups: {},
    customGroups: {},
  });
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [prevGroups, setPrevGroups] = useState(null);

  const getClazzesByGrade = useCallback(value => compose(
    map(clazzGroup => clazzGroup.id),
    filter(clazzGroup => clazzGroup.grade === toNumber(value)),
    get('clazzGroups'),
  )(groups.data), [groups.data]);

  const updateSelectedTargets = useCallback(() => {
    const targetsByClazzGroups = filter(target => compose(
      includes(get('id', target.class_group)),
      map(toNumber),
      keys,
      pickBy(identity),
    )(selectedGroups.clazzGroups), targets.data);

    const targetsByCustomGroups = filter(target => compose(
      customGroups => !isEmpty(customGroups),
      intersection(map(customGroup => customGroup.id, target.custom_groups)),
      map(toNumber),
      keys,
      pickBy(identity),
    )(selectedGroups.customGroups), targets.data);

    setSelectedTargets(prevSelectedTargets => uniqBy(target => target.id, [
      ...prevSelectedTargets,
      ...unionBy(target => target.id, targetsByClazzGroups, targetsByCustomGroups),
    ]));
  }, [selectedGroups, targets.data]);

  const handleAllGroupChange = useCallback(() => {
    setSelectedGroups((prevSelectedGroups) => {
      const all = every(identity, prevSelectedGroups.clazzGroups)
      && every(identity, prevSelectedGroups.customGroups);

      return {
        clazzGroups: mapValues(() => !all, prevSelectedGroups.clazzGroups),
        customGroups: mapValues(() => !all, prevSelectedGroups.customGroups),
      };
    });
  }, []);

  const handleGradeGroupChange = useCallback((event) => {
    const clazzes = getClazzesByGrade(event.currentTarget.dataset.value);

    setSelectedGroups((prevSelectedGroups) => {
      const all = every(clazz => prevSelectedGroups.clazzGroups[clazz], clazzes);

      return {
        clazzGroups: {
          ...prevSelectedGroups.clazzGroups,
          ...reduce((acc, cur) => ({ ...acc, [cur]: !all }), {}, clazzes),
        },
        customGroups: prevSelectedGroups.customGroups,
      };
    });
  }, [getClazzesByGrade]);

  const handleClazzGroupChange = useCallback((event) => {
    setSelectedGroups(prevSelectedGroups => ({
      clazzGroups: {
        ...prevSelectedGroups.clazzGroups,
        [event.currentTarget.dataset.value]: !prevSelectedGroups.clazzGroups[event.currentTarget.dataset.value],
      },
      customGroups: prevSelectedGroups.customGroups,
    }));
  }, []);

  const handleCustomGroupChange = useCallback((event) => {
    setSelectedGroups(prevSelectedGroups => ({
      clazzGroups: prevSelectedGroups.clazzGroups,
      customGroups: {
        ...prevSelectedGroups.customGroups,
        [event.currentTarget.dataset.value]: !prevSelectedGroups.customGroups[event.currentTarget.dataset.value],
      },
    }));
  }, []);

  const handleAllTargetChange = useCallback(() => {
    setSelectedTargets((prevSelectedTargets) => {
      const all = size(targets.data) === size(prevSelectedTargets);

      return all ? [] : targets.data;
    });

    setSelectedGroups((prevSelectedGroups) => {
      const all = every(identity, prevSelectedGroups.clazzGroups)
        && every(identity, prevSelectedGroups.customGroups);

      return {
        clazzGroups: mapValues(() => !all, prevSelectedGroups.clazzGroups),
        customGroups: mapValues(() => !all, prevSelectedGroups.customGroups),
      };
    });
  }, [targets.data]);

  const handleIndividualTargetChange = useCallback((event) => {
    const { value } = event.currentTarget.dataset;

    const target = find(selectedTarget => selectedTarget.id === toNumber(value), selectedTargets);

    setSelectedTargets(
      target
        ? filter(selectedTarget => selectedTarget.id !== target.id)
        : prevSelectedTargets => ([
          ...prevSelectedTargets,
          find(t => t.id === toNumber(value), targets.data),
        ]),
    );

    setSelectedGroups((prevSelectedGroups) => {
      if (target) {
        return {
          clazzGroups: {
            ...prevSelectedGroups.clazzGroups,
            ...(target.class_group ? { [target.class_group.id]: false } : null),
          },
          customGroups: {
            ...prevSelectedGroups.customGroups,
            ...reduce((acc, cur) => ({ ...acc, [cur.id]: false }), {}, target.custom_groups),
          },
        };
      }

      const selectedTarget = find(t => t.id === toNumber(value), targets.data);

      const clazzGroup = get('id', selectedTarget.class_group);
      const customGroups = map(customGroup => customGroup.id, selectedTarget.custom_groups);

      const targetLengthByClazzGroup = size(filter(t => get('id', t.class_group) === clazzGroup, targets.data));
      const selectedTargetLengthByClazzGroup = size(filter(t => get('id', t.class_group) === clazzGroup, selectedTargets));

      return {
        clazzGroups: {
          ...prevSelectedGroups.clazzGroups,
          [clazzGroup]: targetLengthByClazzGroup === selectedTargetLengthByClazzGroup + 1,
        },
        customGroups: {
          ...prevSelectedGroups.customGroups,
          ...reduce((acc, cur) => {
            const targetLengthByCustomGroup = size(filter(t => intersection(
              map(customGroup => customGroup.id, t.custom_groups), customGroups,
            ), targets.data));
            const selectedTargetLengthByCustomGroup = size(filter(t => intersection(
              map(customGroup => customGroup.id, t.custom_groups), customGroups,
            ), selectedTargets));

            return {
              ...acc,
              [cur]: targetLengthByCustomGroup === selectedTargetLengthByCustomGroup,
            };
          }, {}, customGroups),
        },
      };
    });
  }, [selectedTargets, targets.data]);

  const handleClick = useCallback(() => {
    form.setFieldValue('targets', isEmpty(selectedTargets) ? null : selectedTargets);

    onClose();
  }, [form, selectedTargets, onClose]);

  if (groups.data !== prevGroups) {
    setSelectedGroups({
      clazzGroups: compose(
        mapValues(() => false),
        keyBy('id'),
        get('clazzGroups'),
      )(groups.data),
      customGroups: compose(
        mapValues(() => false),
        keyBy('id'),
        get('customGroups'),
      )(groups.data),
    });

    setPrevGroups(groups.data);
  }

  useEffect(() => {
    updateSelectedTargets();
  }, [updateSelectedTargets]);

  return (
    <>
      <S.Dialog open={open} onClose={onClose}>
        <S.DialogTitle>
          <S.Heading>
            전송 대상
          </S.Heading>
        </S.DialogTitle>
        <S.DialogContent>
          <S.Note>*가정통신문은 주소록에 등록된 학생의 대표 보호자에게 전송됩니다.</S.Note>
          <S.Flexbox>
            <S.Padder>
              <S.MenuList>
                <S.MenuItem disableRipple>
                  <S.Checkbox
                    color="primary"
                    checked={every(identity, selectedGroups.clazzGroups)
                      && every(identity, selectedGroups.customGroups)}
                    onChange={handleAllGroupChange}
                    margined
                  />
                  전체
                </S.MenuItem>
                {compose(
                  map.convert({ cap: false })((gradeGroup, key) => (
                    <React.Fragment key={key}>
                      <S.MenuItem disableRipple>
                        <S.Checkbox
                          color="primary"
                          checked={every(clazz => selectedGroups.clazzGroups[clazz], getClazzesByGrade(key))}
                          onChange={handleGradeGroupChange}
                          inputProps={{ 'data-value': key }}
                          margined
                        />
                        {`${key}학년`}
                      </S.MenuItem>
                      {map(clazz => (
                        <S.SubmenuItem key={clazz.id} disableRipple>
                          <S.Checkbox
                            color="primary"
                            checked={selectedGroups.clazzGroups[clazz.id]}
                            onChange={handleClazzGroupChange}
                            inputProps={{ 'data-value': clazz.id }}
                            margined
                          />
                          {`${clazz.class_name}반`}
                        </S.SubmenuItem>
                      ), gradeGroup)}
                    </React.Fragment>
                  )),
                  groupBy(clazzGroup => clazzGroup.grade),
                  get('clazzGroups'),
                )(groups.data)}
                {compose(
                  map(customGroup => (
                    <S.MenuItem key={customGroup.id} disableRipple>
                      <S.Checkbox
                        color="primary"
                        checked={selectedGroups.customGroups[customGroup.id]}
                        onChange={handleCustomGroupChange}
                        inputProps={{ 'data-value': customGroup.id }}
                        margined
                      />
                      {customGroup.name || '미지정'}
                    </S.MenuItem>
                  )),
                  sortBy(['is_unspecified_group']),
                  get('customGroups'),
                )(groups.data)}
              </S.MenuList>
            </S.Padder>
            <S.TargetList>
              <S.Table>
                <S.TableHead>
                  <S.TableRow>
                    <S.TableCell as="th">
                      <S.Checkbox
                        checked={size(targets.data) === size(selectedTargets)}
                        onChange={handleAllTargetChange}
                        color="primary"
                      />
                    </S.TableCell>
                    <S.TableCell as="th">
                      그룹명
                    </S.TableCell>
                    <S.TableCell as="th">
                      번호
                    </S.TableCell>
                    <S.TableCell as="th">
                      학생 이름
                    </S.TableCell>
                  </S.TableRow>
                </S.TableHead>
                <S.TableBody>
                  {map(target => (
                    <S.TableRow key={target.id}>
                      <S.TableCell>
                        <S.Checkbox
                          checked={!!find(selectedTarget => selectedTarget.id === target.id, selectedTargets)}
                          onChange={handleIndividualTargetChange}
                          inputProps={{ 'data-value': target.id }}
                          color="primary"
                        />
                      </S.TableCell>
                      <S.TableCell>
                        {target.class_group
                          ? `${target.class_group.grade}학년 ${target.class_group.class_name}반`
                          : getOr('미지정', 'name', maxBy(customGroup => Date.parse(customGroup.created_at), target.custom_groups))}
                      </S.TableCell>
                      <S.TableCell>
                        {target.number || '-'}
                      </S.TableCell>
                      <S.TableCell>
                        {target.name}
                      </S.TableCell>
                    </S.TableRow>
                  ), targets.data)}
                </S.TableBody>
              </S.Table>
            </S.TargetList>
          </S.Flexbox>
          <S.TargetCounter>
            {`총 ${size(targets.data)}명 중 `}
            <S.Selected>
              {`${size(selectedTargets)}명 선택`}
            </S.Selected>
          </S.TargetCounter>
        </S.DialogContent>
        <DialogActions>
          <S.IconButton type="button" size="small" onClick={onClose}>
            <S.Clear />
          </S.IconButton>
          <S.Button type="button" variant="outlined" onClick={onClose}>
            취소
          </S.Button>
          <S.Button
            type="button"
            color="primary"
            variant="contained"
            onClick={handleClick}
          >
            확인
          </S.Button>
        </DialogActions>
      </S.Dialog>
      {(groups.loading || targets.loading) && open && <Spinner />}
    </>
  );
};

TargetSelectDialog.propTypes = {
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  groups: PropTypes.shape({
    data: PropTypes.shape({
      clazzGroups: PropTypes.arrayOf(PropTypes.shape({
        class_name: PropTypes.string,
        contacts_count: PropTypes.number,
        created_at: PropTypes.string,
        grade: PropTypes.number,
        id: PropTypes.number,
        updated_at: PropTypes.string,
      })),
      customGroups: PropTypes.arrayOf(PropTypes.shape({
        contacts_count: PropTypes.number,
        created_at: PropTypes.string,
        id: PropTypes.number,
        is_unspecified_group: PropTypes.bool,
        name: PropTypes.string,
        updated_at: PropTypes.string,
      })),
    }),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.shape({}),
  }).isRequired,
  targets: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      class_group: PropTypes.shape({
        class_name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        contacts_count: PropTypes.number,
        created_at: PropTypes.string,
        grade: PropTypes.number,
        id: PropTypes.number,
        updated_at: PropTypes.string,
      }),
      class_name: PropTypes.string,
      created_at: PropTypes.string,
      custom_groups: PropTypes.arrayOf(PropTypes.shape({
        contacts_count: PropTypes.number,
        created_at: PropTypes.string,
        id: PropTypes.number,
        is_unspecified_group: PropTypes.bool,
        name: PropTypes.string,
        updated_at: PropTypes.string,
      })),
      email: PropTypes.string,
      grade: PropTypes.number,
      id: PropTypes.number,
      mobile: PropTypes.string,
      name: PropTypes.string,
      number: PropTypes.number,
      primary_guardian_contact: PropTypes.shape({
        contact_id: PropTypes.number,
        created_at: PropTypes.string,
        email: PropTypes.string,
        id: PropTypes.number,
        level: PropTypes.string,
        mobile: PropTypes.string,
        name: PropTypes.string,
        relationship: PropTypes.string,
        updated_at: PropTypes.string,
        user_id: PropTypes.number,
      }),
    })),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.shape({}),
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TargetSelectDialog;
